import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Eye,
  Trash2,
  Calendar,
  User,
  Clock,
  Receipt,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  DollarSign,
  Users,
  Timer,
  ShoppingBag,
  AlertCircle,
  CheckCircle,
  XCircle,
  Minus,
  Plus,
  CreditCard,
  Banknote,
  Smartphone,
  ArrowLeftRight,
  Download,
  SlidersHorizontal,
  Loader2,
  Wallet
} from 'lucide-react';
import { extractBusinessType, getStoredBusinessType } from "../utils/businessType";
import { SaleVoidDialog, SaleVoidHistoryDialog } from "./SaleVoidDialog";

import ReservesDashboard from "./ReservesDashboard";

const SalesInterface = () => {
  const location = useLocation();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 25,
    total: 0,
    total_pages: 0
  });
  const currentPageRef = useRef(1);

  // États pour les filtres
  const [filters, setFilters] = useState({
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    cashier_id: '',
    sale_type: '',
    search: ''
  });

  // États pour les données de filtres
  const [cashiers, setCashiers] = useState([]);
  const [serviceHours, setServiceHours] = useState([]);

  // États pour le modal de détails
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [saleDetails, setSaleDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [voidSale, setVoidSale] = useState(null);
  const [showVoidHistory, setShowVoidHistory] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [activeTab, setActiveTab] = useState("sales");
  const [creditSaleEnabled, setCreditSaleEnabled] = useState(false);
  const [adminCanDeleteSales, setAdminCanDeleteSales] = useState(false);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryRows, setRecoveryRows] = useState([]);
  const [recoveryStats, setRecoveryStats] = useState({
    total_due: 0,
    total_tickets: 0,
    total_creditors: 0,
  });
  const [recoveryPagination, setRecoveryPagination] = useState({
    current_page: 1,
    per_page: 25,
    total: 0,
    total_pages: 1,
  });
  const [recoveryFilters, setRecoveryFilters] = useState({
    status: "all",
    search: "",
  });
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const superUser = localStorage.getItem("moo_user_superadmin");
      const user = localStorage.getItem("moo_user");
      return JSON.parse(superUser || user || "{}");
    } catch (_e) {
      return {};
    }
  });
  const [currentCashSession, setCurrentCashSession] = useState(() => {
    try {
      const raw = localStorage.getItem("moo_cash_session");
      return raw ? JSON.parse(raw) : null;
    } catch (_e) {
      return null;
    }
  });
  const [showRecoveryTimelineModal, setShowRecoveryTimelineModal] = useState(false);
  const [recoveryTimelineLoading, setRecoveryTimelineLoading] = useState(false);
  const [recoveryTimelineRows, setRecoveryTimelineRows] = useState([]);
  const [timelineTarget, setTimelineTarget] = useState(null);
  const [showRecoveryPayModal, setShowRecoveryPayModal] = useState(false);
  const [recoveryPayTarget, setRecoveryPayTarget] = useState(null);
  const [recoveryPayAmount, setRecoveryPayAmount] = useState("");
  const [recoveryPayMethod, setRecoveryPayMethod] = useState("");
  const [recoveryPayNote, setRecoveryPayNote] = useState("");
  const [recoveryPayPin, setRecoveryPayPin] = useState("");
  const [recoverySubmitting, setRecoverySubmitting] = useState(false);
  const [recoveryError, setRecoveryError] = useState("");
  const [recoverySuccess, setRecoverySuccess] = useState("");
  const [businessType, setBusinessType] = useState(getStoredBusinessType);
  const isBoutique = businessType === "boutique";
  const [isDarkMode, setIsDarkMode] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : true,
  );

  useEffect(() => {
    const target = document.documentElement;
    const updateMode = () => {
      setIsDarkMode(target.classList.contains("dark"));
    };
    updateMode();
    const observer = new MutationObserver(updateMode);
    observer.observe(target, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const loadBusinessType = async () => {
      try {
        const res = await fetch("/api/restaurant-info");
        if (res.ok) {
          const data = await res.json();
          setBusinessType(extractBusinessType(data));
          try {
            localStorage.setItem("restaurant-info", JSON.stringify(data || {}));
          } catch (_e) {}
          return;
        }
      } catch (_e) {
        // ignore
      }
      try {
        const stored = localStorage.getItem("restaurant-info");
        if (stored) {
          setBusinessType(extractBusinessType(JSON.parse(stored)));
        }
      } catch (_e) {
        // ignore
      }
    };
    loadBusinessType();
  }, []);

  const parseBooleanFlag = (value, fallback = false) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (normalized === "true" || normalized === "1") return true;
      if (normalized === "false" || normalized === "0") return false;
    }
    return fallback;
  };

  const isCreditMethodCode = (value = "") => {
    const normalized = value.toString().trim().toLowerCase();
    return (
      normalized === "credit" ||
      normalized === "a-credit" ||
      normalized === "à-crédit" ||
      normalized.includes("credit") ||
      normalized.includes("crédit")
    );
  };

  const loadCreditSaleSetting = async () => {
    try {
      const response = await fetch(`/api/settings/credit-sale?_=${Date.now()}`, {
        cache: "no-store",
      });
      if (!response.ok) return;
      const data = await response.json();
      setCreditSaleEnabled(parseBooleanFlag(data?.enabled, false));
    } catch (_e) {
      // non bloquant
    }
  };

  const loadAdminOptions = async () => {
    try {
      const response = await fetch(`/api/settings/admin-options?_=${Date.now()}`, {
        cache: "no-store",
      });
      if (!response.ok) return;
      const data = await response.json();
      setAdminCanDeleteSales(parseBooleanFlag(data?.admin_can_delete_sales, false));
    } catch (_e) {
      setAdminCanDeleteSales(false);
    }
  };

  useEffect(() => {
    const syncFromStorage = () => {
      try {
        const superUser = localStorage.getItem("moo_user_superadmin");
        const user = localStorage.getItem("moo_user");
        setCurrentUser(JSON.parse(superUser || user || "{}"));
      } catch (_e) {
        setCurrentUser({});
      }
      try {
        const raw = localStorage.getItem("moo_cash_session");
        setCurrentCashSession(raw ? JSON.parse(raw) : null);
      } catch (_e) {
        setCurrentCashSession(null);
      }
    };

    const handlePaymentSettingsUpdated = (event) => {
      const enabled = event?.detail?.creditSaleEnabled;
      if (typeof enabled === "boolean") {
        setCreditSaleEnabled(enabled);
      } else {
        void loadCreditSaleSetting();
      }
    };

    const handleAdminOptionsUpdated = (event) => {
      const enabled = event?.detail?.admin_can_delete_sales;
      if (typeof enabled === "boolean") {
        setAdminCanDeleteSales(enabled);
      } else {
        void loadAdminOptions();
      }
    };

    syncFromStorage();
    void loadCreditSaleSetting();
    void loadAdminOptions();
    window.addEventListener("storage", syncFromStorage);
    window.addEventListener("moo-payment-settings-updated", handlePaymentSettingsUpdated);
    window.addEventListener("moo-admin-options-updated", handleAdminOptionsUpdated);
    return () => {
      window.removeEventListener("storage", syncFromStorage);
      window.removeEventListener(
        "moo-payment-settings-updated",
        handlePaymentSettingsUpdated,
      );
      window.removeEventListener(
        "moo-admin-options-updated",
        handleAdminOptionsUpdated,
      );
    };
  }, []);

  // Icônes pour les moyens de paiement
  const PAYMENT_ICONS = {
    'cash': Banknote,
    'card': CreditCard,
    'mobile': Smartphone,
    'transfer': ArrowLeftRight,
    'credit': Receipt
  };

  const formatFilterDate = (value) => {
    if (!value) return "";
    const parts = value.split("-");
    if (parts.length !== 3) return value;
    return `${parts[2]}/${parts[1]}`;
  };

  const activeFiltersCount = [
    filters.start_date || filters.start_time ? 1 : 0,
    filters.end_date || filters.end_time ? 1 : 0,
    filters.cashier_id ? 1 : 0,
    !isBoutique && filters.sale_type ? 1 : 0,
    filters.search && filters.search.trim() ? 1 : 0,
  ].reduce((total, value) => total + value, 0);

  const saleTypeOptions = [
    { value: '', label: 'Tous les types' },
    { value: 'dine_in', label: 'Sur place' },
    { value: 'delivery_direct', label: 'Livraison - Direct' },
    { value: 'delivery_yango', label: 'Livraison - Yango' },
    { value: 'delivery_glovo', label: 'Livraison - Glovo' },
  ];

  const normalizePaymentMethod = (method) =>
    (method || "").toString().trim();

  const normalize = (value) => normalizePaymentMethod(value).toLowerCase();

  const findConfiguredMethod = (method) => {
    const target = normalize(method);
    if (!target) return null;
    return (
      paymentMethods.find((m) => normalize(m.code) === target) ||
      paymentMethods.find((m) => normalize(m.name) === target) ||
      paymentMethods.find(
        (m) =>
          target.includes(normalize(m.code)) || target.includes(normalize(m.name)),
      )
    );
  };

  const formatPaymentMethodLabel = (method) => {
    const configured = findConfiguredMethod(method);
    if (configured?.name) return configured.name;

    const raw = normalizePaymentMethod(method);
    const normalized = normalize(raw);
    if (!normalized) return "—";

    const contains = (token) => normalized.includes(token);
    if (normalized === "fully_discounted") return "Remise 100 %";
    if (normalized === "complimentary") return "Offert";
    if (contains("credit") || contains("crédit")) {
      return "À crédit";
    }
    if (
      contains("esp") ||
      contains("cash") ||
      contains("espece") ||
      contains("espèces")
    ) {
      return "Espèces";
    }
    if (
      contains("carte") ||
      contains("cb") ||
      contains("visa") ||
      contains("master") ||
      contains("card")
    ) {
      return "Carte bancaire";
    }
    if (contains("orange") || normalized === "om" || normalized === "o.m") {
      return "OM";
    }
    if (contains("wave")) {
      return "Wave";
    }
    if (contains("mtn") || contains("momo")) {
      return "MTN MoMo";
    }
    if (contains("moov") || contains("flooz")) {
      return "Moov Money";
    }
    if (contains("free")) {
      return "Free Money";
    }
    if (contains("virement") || contains("transfert") || contains("transfer")) {
      return "Virement";
    }
    if (contains("cheque") || contains("chèque")) {
      return "Chèque";
    }
    if (contains("multi")) {
      return "—";
    }
    if (contains("mobile")) {
      return "—";
    }
    return raw || "—";
  };

  const formatPaymentMethodList = (methods, fallback) => {
    const labels = Array.isArray(methods)
      ? methods.map(formatPaymentMethodLabel).filter(Boolean)
      : [];
    if (!labels.length && fallback) {
      labels.push(formatPaymentMethodLabel(fallback));
    }
    const unique = Array.from(new Set(labels));
    const filtered = unique.filter(
      (label) => !label.toLowerCase().includes("multi"),
    );
    const finalList = filtered.length > 0 ? filtered : unique;
    return finalList.join(", ");
  };

  const computeItemExtrasTotal = (item) => {
    if (!item?.extras || !Array.isArray(item.extras)) return 0;
    return item.extras.reduce((sum, extra) => {
      const qty = Number(extra.quantity) || 1;
      const value =
        Number(extra.total_price) ||
        (Number(extra.unit_price ?? extra.price) || 0) * qty;
      return sum + value;
    }, 0);
  };

  const computeItemTotalWithExtras = (item) => {
    const quantity = Number(item.quantity) || 1;
    const totalWithExtras = Number(item.total_price_with_extras);
    if (Number.isFinite(totalWithExtras) && totalWithExtras > 0) {
      return totalWithExtras;
    }
    const baseTotal =
      Number(item.total_price) ||
      (Number(item.unit_price ?? item.price) || 0) * quantity;
    return baseTotal + computeItemExtrasTotal(item);
  };

  const parsePaymentBreakdownFromNotes = (notes) => {
    if (!notes) return [];
    const marker = "MOO_BREAKDOWN=";
    const idx = notes.indexOf(marker);
    if (idx === -1) return [];
    const after = notes.slice(idx + marker.length).trim();
    const payload = after.split("|")[0].trim();
    if (!payload) return [];
    return payload
      .split(",")
      .map((entry) => entry.trim())
      .map((entry) => {
        const [id, rawAmount] = entry.split(":");
        if (!id) return null;
        const amount = parseFloat(rawAmount);
        return {
          method: id,
          amount: Number.isFinite(amount) ? amount : null,
        };
      })
      .filter(Boolean);
  };

  const loadPaymentMethods = async () => {
    try {
      const response = await fetch("/api/payment-methods/all");
      if (!response.ok) return;
      const data = await response.json();
      if (Array.isArray(data)) {
        setPaymentMethods(data);
      }
    } catch (_e) {
      // ignorer
    }
  };

  const recoveryPaymentMethods = useMemo(
    () => (paymentMethods || []).filter((method) => !isCreditMethodCode(method?.code || method?.id || method?.name)),
    [paymentMethods],
  );

  const loadRecoveryRows = async (pageNumber = 1, nextFilters = recoveryFilters) => {
    if (!creditSaleEnabled) return;
    try {
      setRecoveryLoading(true);
      setRecoveryError("");

      const params = new URLSearchParams({
        page: `${pageNumber}`,
        limit: `${recoveryPagination.per_page || 25}`,
      });
      if (nextFilters?.search?.trim()) {
        params.set("search", nextFilters.search.trim());
      }
      if (nextFilters?.status && nextFilters.status !== "all") {
        params.set("status", nextFilters.status);
      }

      const response = await fetch(`/api/credit-recovery/receivables?${params.toString()}`, {
        cache: "no-store",
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error || "Impossible de charger les recouvrements");
      }
      const data = await response.json();
      setRecoveryRows(Array.isArray(data?.receivables) ? data.receivables : []);
      setRecoveryStats({
        total_due: Number(data?.stats?.total_due || 0),
        total_tickets: Number(data?.stats?.total_tickets || 0),
        total_creditors: Number(data?.stats?.total_creditors || 0),
      });
      setRecoveryPagination((prev) => ({
        ...prev,
        ...(data?.pagination || {}),
      }));
    } catch (error) {
      setRecoveryError(error?.message || "Erreur serveur");
    } finally {
      setRecoveryLoading(false);
    }
  };

  const loadRecoveryTimeline = async (receivableId) => {
    if (!receivableId || !creditSaleEnabled) return;
    try {
      setRecoveryTimelineLoading(true);
      setRecoveryError("");
      const response = await fetch(
        `/api/credit-recovery/receivables/${receivableId}/history`,
        { cache: "no-store" },
      );
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error || "Impossible de charger l'historique");
      }
      const data = await response.json();
      setTimelineTarget(data?.receivable || null);
      setRecoveryTimelineRows(Array.isArray(data?.timeline) ? data.timeline : []);
      setShowRecoveryTimelineModal(true);
    } catch (error) {
      setRecoveryError(error?.message || "Erreur serveur");
    } finally {
      setRecoveryTimelineLoading(false);
    }
  };

  const openRecoveryPayModal = (target) => {
    const due = Number(target?.amount_due ?? target?.due_amount ?? 0);
    setRecoveryPayTarget(target || null);
    setRecoveryPayAmount(due > 0 ? `${Math.round(due)}` : "");
    setRecoveryPayMethod("");
    setRecoveryPayNote("");
    setRecoveryPayPin("");
    setRecoveryError("");
    setRecoverySuccess("");
    setShowRecoveryPayModal(true);
  };

  const openRecoveryTicket = (row) => {
    if (!row?.order_id) {
      setRecoveryError("Ticket introuvable");
      return;
    }
    void handleViewDetails({
      id: row.order_id,
      order_id: row.order_id,
      order_number: row.order_number,
      payment_number: row.reference_number || row.payment_number || row.order_number,
      payment_created_at: row.order_created_at || row.created_at,
    });
  };

  const submitRecoveryPayment = async () => {
    if (!recoveryPayTarget) return;
    const normalizedAmount = Number(recoveryPayAmount);
    if (!Number.isFinite(normalizedAmount) || normalizedAmount <= 0) {
      setRecoveryError("Montant invalide");
      return;
    }
    if (!recoveryPayMethod || isCreditMethodCode(recoveryPayMethod)) {
      setRecoveryError("Moyen de paiement invalide");
      return;
    }
    if (!recoveryPayPin.trim()) {
      setRecoveryError("Code de validation requis");
      return;
    }
    if (!currentUser?.id) {
      setRecoveryError("Utilisateur non identifié");
      return;
    }

    try {
      setRecoverySubmitting(true);
      setRecoveryError("");
      setRecoverySuccess("");
      const payload = {
        amount: normalizedAmount,
        paymentMethod: recoveryPayMethod,
        notes: recoveryPayNote?.trim() || null,
        pinCode: recoveryPayPin.trim(),
        userId: currentUser.id,
        sessionId: currentCashSession?.id || null,
      };

      if (recoveryPayTarget?.creditor_ref) {
        payload.creditorRef = recoveryPayTarget.creditor_ref;
      } else if (recoveryPayTarget?.id || recoveryPayTarget?.receivable_id) {
        payload.receivableId = recoveryPayTarget.id || recoveryPayTarget.receivable_id;
      } else {
        setRecoveryError("Cible de recouvrement invalide");
        return;
      }

      const response = await fetch("/api/credit-recovery/pay", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.error || "Échec du recouvrement");
      }

      setRecoverySuccess("Recouvrement enregistré avec succès");
      setShowRecoveryPayModal(false);
      await loadRecoveryRows(recoveryPagination.current_page || 1);
      if (showRecoveryTimelineModal && (timelineTarget?.id || recoveryPayTarget?.id)) {
        await loadRecoveryTimeline(timelineTarget?.id || recoveryPayTarget?.id);
      }
    } catch (error) {
      setRecoveryError(error?.message || "Erreur serveur");
    } finally {
      setRecoverySubmitting(false);
    }
  };

  const deliveryDetailsLabel =
    saleDetails?.order ? formatDeliveryCustomerInfo(saleDetails.order) : null;

  const isSuperadminView = location.pathname.startsWith("/superadmin");
  const canDeleteSale = adminCanDeleteSales;

  useEffect(() => {
    loadFilterData();
    loadPaymentMethods();
  }, []);

  useEffect(() => {
    if (activeTab !== "sales") return;
    loadSales();
  }, [activeTab, pagination.current_page, filters]);

  useEffect(() => {
    if (activeTab !== "sales") return undefined;
    if (!isSuperadminView) return undefined;
    const interval = setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      backgroundReloadFullPage(currentPageRef.current);
    }, 8000);
    return () => clearInterval(interval);
  }, [activeTab, isSuperadminView, filters, pagination.per_page]);

  useEffect(() => {
    if (!creditSaleEnabled) {
      if (activeTab === "recovery") setActiveTab("sales");
      return;
    }
    if (activeTab !== "recovery") return;
    void loadRecoveryRows(recoveryPagination.current_page || 1, recoveryFilters);
  }, [activeTab, creditSaleEnabled, recoveryPagination.current_page, recoveryFilters]);

  const buildFilterParams = () => {
    const params = {};
    if (filters.start_date) {
      params.start_date = filters.start_date;
      if (filters.start_time) {
        params.start_time = filters.start_time;
      }
    }
    if (filters.end_date) {
      params.end_date = filters.end_date;
      if (filters.end_time) {
        params.end_time = filters.end_time;
      }
    }
    if (filters.cashier_id) {
      params.cashier_id = filters.cashier_id;
    }
    if (filters.sale_type) {
      params.sale_type = filters.sale_type;
    }
    if (filters.search && filters.search.trim()) {
      params.search = filters.search.trim();
    }
    return params;
  };

  const firstLoadRef = useRef(true);
  useEffect(() => {
    currentPageRef.current = pagination.current_page;
  }, [pagination.current_page]);

  const backgroundReloadFullPage = async (pageNumber) => {
    try {
      const paramsObject = {
        page: pageNumber.toString(),
        limit: pagination.per_page.toString(),
      };
      const activeFilters = buildFilterParams();
      Object.entries(activeFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          paramsObject[key] = value;
        }
      });
      const params = new URLSearchParams(paramsObject);
      const resp = await fetch(`/api/sales?${params}`);
      if (resp.ok) {
        const data = await resp.json();
        setPagination((prev) => ({
          ...prev,
          ...data.pagination,
          current_page: prev.current_page,
        }));
        if (currentPageRef.current === pageNumber) {
          setSales(data.sales);
        }
      }
    } catch (err) {
      console.warn("⚠️ Reload complet (background) ignoré:", err?.message);
    }
  };

  const loadSales = async () => {
    try {
      setLoading(true);
      console.log('🔄 Chargement des ventes...');

      const isFastFirstPage =
        firstLoadRef.current && pagination.current_page === 1;

      const paramsObject = {
        page: pagination.current_page.toString(),
        limit: pagination.per_page.toString(),
      };
      if (isFastFirstPage) {
        paramsObject.fast = "1"; // réponse plus légère pour afficher vite
      }
      const activeFilters = buildFilterParams();
      Object.entries(activeFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          paramsObject[key] = value;
        }
      });
      const params = new URLSearchParams(paramsObject);

      const response = await fetch(`/api/sales?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Ventes reçues:', data);
        setSales(data.sales);
        setPagination(data.pagination);

        // Après affichage rapide, recharger la page en complet en arrière-plan
        if (isFastFirstPage) {
          firstLoadRef.current = false;
          backgroundReloadFullPage(1);
        }
      } else {
        console.error('❌ Erreur lors du chargement des ventes:', response.status);
      }
    } catch (error) {
      console.error('❌ Erreur API ventes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFilterData = async () => {
    try {
      // Charger les caissiers
      const cashiersResponse = await fetch('/api/sales/cashiers');
      if (cashiersResponse.ok) {
        const cashiersData = await cashiersResponse.json();
        setCashiers(cashiersData);
      }

      // Charger les horaires de service
      const hoursResponse = await fetch('/api/service-hours');
      if (hoursResponse.ok) {
        const hoursData = await hoursResponse.json();
        setServiceHours(hoursData);
      }
    } catch (error) {
      console.error('❌ Erreur chargement données filtres:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const resetFilters = () => {
    setFilters({
      start_date: '',
      start_time: '',
      end_date: '',
      end_time: '',
      cashier_id: '',
      sale_type: '',
      search: ''
    });
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handleRecoveryFilterChange = (key, value) => {
    setRecoveryFilters((prev) => ({ ...prev, [key]: value }));
    setRecoveryPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const getRecoveryStatusLabel = (status) => {
    const normalized = (status || "").toString().trim().toLowerCase();
    if (normalized === "paye") return "Payé";
    if (normalized === "partiel") return "Partiel";
    return "En cours";
  };

  const getRecoveryStatusClass = (status) => {
    const normalized = (status || "").toString().trim().toLowerCase();
    if (normalized === "paye") {
      return "bg-emerald-500/15 text-emerald-300 border border-emerald-400/30";
    }
    if (normalized === "partiel") {
      return "bg-amber-500/15 text-amber-300 border border-amber-400/30";
    }
    return "bg-rose-500/15 text-rose-300 border border-rose-400/30";
  };

  const handleExport = async (view = "orders") => {
    if (exporting) return;
    try {
      setExporting(true);
      const params = new URLSearchParams(buildFilterParams());
      if (view === "items") {
        params.set("view", "items");
      }
      const queryString = params.toString();
      const endpoint = queryString
        ? `/api/sales/export?${queryString}`
        : '/api/sales/export';

      const response = await fetch(endpoint);
      if (!response.ok) {
        throw new Error(`Export failed with status ${response.status}`);
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().replace(/[:]/g, '-').split('.')[0];
      link.href = downloadUrl;
      link.download =
        view === "items"
          ? `ventes-articles-${timestamp}.xls`
          : `ventes-${timestamp}.xls`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('❌ Erreur export ventes:', error);
      window.alert("Impossible d'exporter les ventes pour le moment.");
    } finally {
      setExporting(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, current_page: newPage }));
  };

  const handleRecoveryPageChange = (newPage) => {
    setRecoveryPagination((prev) => ({ ...prev, current_page: newPage }));
  };

  const handleViewDetails = async (sale) => {
    try {
      setSelectedSale(sale);
      setShowDetailsModal(true);
      setLoadingDetails(true);
      setSaleDetails(null);

      console.log('🔄 Chargement détails vente:', sale.id);
      const response = await fetch(`/api/sales/${sale.id}/details`);
      
      if (response.ok) {
        const details = await response.json();
        console.log('✅ Détails vente reçus:', details);
        setSaleDetails(details);
      } else {
        console.error('❌ Erreur chargement détails vente:', response.status);
      }
    } catch (error) {
      console.error('❌ Erreur API détails vente:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleDeleteSale = (sale) => {
    if (canDeleteSale && sale?.id) setVoidSale(sale);
  };

  const formatCurrency = (amount) => {
    const parsed = Number(amount);
    if (Number.isNaN(parsed)) {
      return '0 F';
    }
    return `${new Intl.NumberFormat('fr-FR').format(Math.round(parsed))} F`;
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (minutes) => {
    if (minutes === null || minutes === undefined) return '-';
    const parsed = Number(minutes);
    if (Number.isNaN(parsed) || parsed <= 0) return '-';
    const hours = Math.floor(parsed / 60);
    const mins = Math.floor(parsed % 60);
    const seconds = Math.round((parsed - Math.floor(parsed)) * 60);
    if (hours > 0) {
      return `${hours}h ${mins}min`;
    }
    if (mins > 0) {
      return `${mins}min`;
    }
    return `${seconds}s`;
  };

  const getSaleTypeLabel = (sale) => {
    if (sale?.type_label) return sale.type_label;
    return getOrderTypeLabel(sale?.order_type);
  };

function formatPrepSummary(sale) {
  const global =
    sale?.service_prep_minutes || sale?.avg_preparation_time_minutes;
  if (global) {
    return formatDuration(global);
  }
  return '-';
}

function normalizeText(value) {
  if (value === null || value === undefined) {
    return null;
  }
  const stringValue =
    typeof value === 'string' ? value : value.toString();
  const trimmed = stringValue.trim();
  return trimmed.length > 0 ? trimmed : null;
}

const formatVariationDisplay = (item) => {
  const baseName =
    item.product_name ||
    item.productName ||
    item.name ||
    item.title ||
    'Article';

  const variationLabelRaw =
    item.variation_label ||
    item.variationLabel ||
    item.variation_name ||
    item.variationName ||
    '';
  const variationLabel = variationLabelRaw.toString().trim();

  const attrsRaw = item.variation_attributes || item.variationAttributes;
  let attrText = '';
  if (attrsRaw) {
    try {
      const parsed =
        typeof attrsRaw === 'string' ? JSON.parse(attrsRaw) : attrsRaw;
      if (Array.isArray(parsed)) {
        attrText = parsed
          .map((v) => v && v.toString().trim())
          .filter(Boolean)
          .join(' / ');
      } else if (parsed && typeof parsed === 'object') {
        attrText = Object.values(parsed)
          .map((v) => v && v.toString().trim())
          .filter(Boolean)
          .join(' / ');
      }
    } catch (_) {
      // ignore parse error
    }
  }

  const isMeaningful = (val) => {
    if (!val) return false;
    const lower = val.toLowerCase();
    if (lower === 'variation' || lower.startsWith('variation ')) return false;
    if (val === baseName) return false;
    return true;
  };

  if (isMeaningful(attrText)) return `${baseName} · ${attrText}`;
  if (isMeaningful(variationLabel)) return `${baseName} · ${variationLabel}`;
  return baseName;
};

const formatItemName = (item) => formatVariationDisplay(item);

function parseDeliveryCustomerData(raw) {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function formatDeliveryCustomerInfo(order) {
  if (!order) return null;

  const deliveryMode = normalizeText(order.delivery_mode);
  const isDeliveryOrder =
    order.order_type === 'delivery' ||
    order.is_delivery ||
    deliveryMode === 'delivery';
  if (!isDeliveryOrder) {
    return null;
  }

  const deliveryData =
    parseDeliveryCustomerData(order.delivery_customer_data) ||
    order.delivery_customer ||
    order.customer ||
    null;

  const reference =
    normalizeText(order.delivery_reference) ||
    normalizeText(order.deliveryReference) ||
    normalizeText(deliveryData?.delivery_reference) ||
    normalizeText(deliveryData?.reference) ||
    normalizeText(deliveryData?.code);

  const firstName =
    normalizeText(deliveryData?.first_name) ||
    normalizeText(deliveryData?.firstName);
  const lastName =
    normalizeText(deliveryData?.last_name) ||
    normalizeText(deliveryData?.lastName);
  const combinedName = normalizeText(
    [firstName, lastName].filter(Boolean).join(' ')
  );
  const resolvedName =
    normalizeText(deliveryData?.name) ||
    combinedName ||
    normalizeText(order.customer_name) ||
    normalizeText(order.customerName);

  const phone =
    normalizeText(deliveryData?.phone) ||
    normalizeText(deliveryData?.contact) ||
    normalizeText(deliveryData?.phone_number) ||
    normalizeText(order.customer_phone) ||
    normalizeText(order.customerPhone);

  const methodRaw =
    normalizeText(order.delivery_method) ||
    normalizeText(order.deliveryMethod);
  const methodLower = methodRaw ? methodRaw.toLowerCase() : null;
  const isAggregatorDelivery =
    methodLower && methodLower !== 'direct';

  if (isAggregatorDelivery) {
    const aggregatorParts = [reference, resolvedName].filter(Boolean);
    return aggregatorParts.length > 0 ? aggregatorParts.join(' • ') : null;
  }

  const directParts = [resolvedName, phone].filter(Boolean);
  if (reference) {
    directParts.push(reference);
  }

  return directParts.length > 0 ? directParts.join(' • ') : null;
}

  const getOrderTypeLabel = (type) => {
    const types = {
      'dine_in': 'Sur place',
      'takeaway': 'À emporter',
      'delivery': 'Livraison'
    };
    return types[type] || type;
  };

  const getPaymentMethodIcon = (method) => {
    const configured = findConfiguredMethod(method);
    if (configured?.icon_name) {
      const icon = (configured.icon_name || "").toLowerCase();
      if (icon.includes("cash") || icon.includes("banknote")) return Banknote;
      if (icon.includes("card") || icon.includes("carte")) return CreditCard;
      if (icon.includes("mobile") || icon.includes("phone")) return Smartphone;
      if (icon.includes("transfer") || icon.includes("virement")) return ArrowLeftRight;
    }

    const lowerMethod = normalize(method);
    if (
      lowerMethod.includes("esp") ||
      lowerMethod.includes("cash") ||
      lowerMethod.includes("espece")
    ) {
      return Banknote;
    }
    if (
      lowerMethod.includes("carte") ||
      lowerMethod.includes("cb") ||
      lowerMethod.includes("visa") ||
      lowerMethod.includes("master") ||
      lowerMethod.includes("card")
    ) {
      return CreditCard;
    }
    if (
      lowerMethod.includes("virement") ||
      lowerMethod.includes("transfert") ||
      lowerMethod.includes("transfer")
    ) {
      return ArrowLeftRight;
    }
    if (
      lowerMethod.includes("credit") ||
      lowerMethod.includes("crédit")
    ) {
      return Receipt;
    }
    if (
      lowerMethod.includes("wave") ||
      lowerMethod.includes("orange") ||
      lowerMethod.includes("mtn") ||
      lowerMethod.includes("momo") ||
      lowerMethod.includes("moov") ||
      lowerMethod.includes("flooz") ||
      lowerMethod.includes("free") ||
      lowerMethod.includes("mobile")
    ) {
      return Smartphone;
    }
    return Receipt;
  };

  const DELIVERY_METHOD_SUFFIX = {
    direct: '-L',
    yango: '-YG',
    glovo: '-GL',
  };

  const buildPaymentSuffix = (sale) => {
    if (!sale?.is_delivery) {
      return '';
    }
    const method = (sale.delivery_method || '').toLowerCase();
    if (!method || method === 'direct') {
      return DELIVERY_METHOD_SUFFIX.direct;
    }
    if (DELIVERY_METHOD_SUFFIX[method]) {
      return DELIVERY_METHOD_SUFFIX[method];
    }
    return `-${method.slice(0, 2).toUpperCase()}`;
  };

  const buildFallbackPaymentNumber = (sale) => {
    const referenceDate =
      sale?.payment_created_at ||
      sale?.order_date ||
      sale?.created_at ||
      sale?.updated_at;
    const rawDate = referenceDate ? new Date(referenceDate) : new Date();
    const safeDate = Number.isNaN(rawDate.getTime()) ? new Date() : rawDate;
    const day = safeDate.getDate().toString().padStart(2, '0');
    const month = (safeDate.getMonth() + 1).toString().padStart(2, '0');
    const year = safeDate.getFullYear().toString().slice(-2);
    const prefix = `#${day}${month}${year}`;

    const candidates = [
      sale?.payment_sequence,
      sale?.order_sequence,
      sale?.sequence,
      sale?.order_id,
      sale?.id,
    ];

    let sequence = candidates
      .map((value) => parseInt(value, 10))
      .find((value) => Number.isFinite(value) && value > 0);

    if (!Number.isFinite(sequence)) {
      const digits = (sale?.order_number || '').replace(/\D/g, '');
      sequence = parseInt(digits, 10);
    }

    if (!Number.isFinite(sequence)) {
      sequence = parseInt(`${safeDate.getTime()}`.slice(-4), 10);
    }

    const suffix = buildPaymentSuffix(sale);
    return `${prefix}${sequence}${suffix}`;
  };

  const getDisplayPaymentNumber = (sale) => {
    if (!sale) return '—';
    const rawNumber = sale.payment_number;
    if (rawNumber && rawNumber.trim().startsWith('#')) {
      return rawNumber.trim();
    }
    if (rawNumber && rawNumber.trim().length > 0) {
      const trimmed = rawNumber.trim();
      return trimmed.startsWith('#') ? trimmed : `#${trimmed.replace(/^#*/, '')}`;
    }
    return '—';
  };

  const computeFinancialBreakdown = (details) => {
    const order = details?.order || {};
    const provided = order?.financials;
    if (provided) {
      return {
        grossSubtotal: Number(provided.gross_subtotal) || 0,
        activeSubtotal: Number(provided.active_subtotal) || 0,
        cancellationsTotal: Number(provided.cancellations_total) || 0,
        discountTotal: Number(provided.discount_total) || 0,
        taxAmount: Number(provided.tax_amount) || 0,
        totalFinal: Number(provided.total_final) || 0,
      };
    }

    const items = Array.isArray(details?.items) ? details.items : [];
    const cancellations = Array.isArray(details?.cancellation_logs)
      ? details.cancellation_logs
      : [];
    const taxAmount = Number(order.tax_amount) || 0;
    const totalFinal =
      Number(order.total_paid_amount) ||
      Number(order.total_amount) ||
      0;
    const isCancelledItem = (item) =>
      String(item?.status || "").toLowerCase() === "cancelled";
    const activeSubtotal = items
      .filter((item) => !isCancelledItem(item))
      .reduce((sum, item) => sum + computeItemTotalWithExtras(item), 0);
    const cancellationsTotal = cancellations.reduce(
      (sum, log) => sum + (Number(log.total_price) || 0),
      0,
    );
    const inferredDiscount = Math.max(
      0,
      activeSubtotal + taxAmount - totalFinal,
    );
    const grossSubtotal = activeSubtotal + cancellationsTotal;
    return {
      grossSubtotal,
      activeSubtotal,
      cancellationsTotal,
      discountTotal: inferredDiscount,
      taxAmount,
      totalFinal,
    };
  };

  const financials = saleDetails
    ? computeFinancialBreakdown(saleDetails)
    : {
        grossSubtotal: 0,
        activeSubtotal: 0,
        cancellationsTotal: 0,
        discountTotal: 0,
        taxAmount: 0,
        totalFinal: 0,
      };

  // Pour l'affichage Paiement/Récapitulatif: le sous-total doit exclure les articles annulés.
  const resolvedSubtotal = financials.activeSubtotal;


  const containerBg =
    isDarkMode
      ? "bg-gradient-to-br from-[#050b18] via-[#09112a] to-[#050b18] text-slate-100"
      : "bg-gradient-to-br from-slate-50 via-white to-slate-50 text-slate-900";
  const cardClasses =
    isDarkMode
      ? "bg-white/5 border border-white/10 shadow-2xl shadow-black/40"
      : "bg-white border border-slate-100 shadow-xl shadow-slate-200/60";
  const inputClasses =
    isDarkMode
      ? "bg-slate-900/70 border border-white/15 text-slate-100 placeholder-slate-400 focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/30"
      : "bg-white border border-slate-300 text-slate-800 placeholder-slate-500 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200/60";
  const modalOverlayClass = isDarkMode
    ? "bg-black/70 backdrop-blur-xl"
    : "bg-slate-900/40 backdrop-blur-xl";
  const modalCardClass = isDarkMode
    ? "bg-[#0f172a] border border-white/10 text-slate-100"
    : "bg-white border border-slate-200 text-slate-900";
  const modalSectionClass = isDarkMode
    ? "bg-white/5 border border-white/10"
    : "bg-slate-50 border border-slate-200";
  const modalSubCardClass = isDarkMode
    ? "bg-white/10 border border-white/10"
    : "bg-white border border-slate-200";
  const textMuted = isDarkMode ? "text-slate-400" : "text-slate-500";
  const pillClass = isDarkMode
    ? "bg-yellow-500/10 text-yellow-300"
    : "bg-yellow-100 text-yellow-800";
  const selectStyle = isDarkMode ? { colorScheme: "dark" } : undefined;
  const tableSecondaryText = isDarkMode ? "text-slate-400" : "text-slate-600";
  const actionButtonClasses = isDarkMode
    ? "rounded-full border border-white/15 bg-white/5 p-2 text-slate-200 transition hover:bg-yellow-500/20 hover:text-white"
    : "rounded-full border border-slate-300 bg-white p-2 text-slate-600 shadow-sm transition hover:border-yellow-400 hover:text-yellow-600";
  const dangerActionButtonClasses = isDarkMode
    ? "rounded-full border border-rose-500/30 bg-rose-500/10 p-2 text-rose-200 transition hover:bg-rose-500/20 hover:text-white"
    : "rounded-full border border-rose-200 bg-rose-50 p-2 text-rose-600 shadow-sm transition hover:border-rose-300 hover:text-rose-700";
  const dangerCardClass = isDarkMode
    ? "bg-red-900/20 border border-red-500/30"
    : "bg-red-50 border border-red-200";
  const textPrimary = isDarkMode ? "text-white" : "text-slate-900";
  const generalInfoFields = useMemo(() => {
    if (!saleDetails) return [];
    const fields = [
      {
        label: "Date & Heure",
        value: formatDateTime(
          saleDetails.order.payment_completed_at ||
            saleDetails.order.updated_at ||
            saleDetails.order.created_at,
        ),
      },
      {
        label: "N° Commande",
        value: getDisplayPaymentNumber(saleDetails.order),
      },
      {
        label: "Type",
        value:
          saleDetails.order.type_label ||
          getOrderTypeLabel(saleDetails.order.order_type),
        extra: deliveryDetailsLabel,
      },
      {
        label: "Serveur",
        value: saleDetails.order.server_name || "—",
      },
      {
        label: "Caissier(s)",
        value:
          saleDetails.order.cashier_names &&
          saleDetails.order.cashier_names.length > 0
            ? saleDetails.order.cashier_names.join(", ")
            : saleDetails.order.server_name || "—",
      },
      {
        label: "Table",
        value: saleDetails.order.table_number
          ? `Table ${saleDetails.order.table_number}`
          : "Non assignée",
      },
      {
        label: "Visiteurs",
        value: saleDetails.order.visitor_count || "—",
      },
      {
        label: "Session",
        value: saleDetails.order.session_name || "Non définie",
      },
      {
        label: "Durée service",
        value: formatDuration(saleDetails.order.service_duration_minutes),
      },
      {
        label: "Temps préparation",
        value: formatPrepSummary(saleDetails.order),
      },
    ];

    if (!isBoutique) return fields;
    const hidden = new Set([
      "Table",
      "Visiteurs",
      "Durée service",
      "Temps préparation",
      "Type",
      "Serveur",
    ]);
    return fields.filter((field) => !hidden.has(field.label));
  }, [saleDetails, deliveryDetailsLabel, isBoutique]);
  const paymentSummaryStats = saleDetails
    ? [
        {
          label: "Total encaissé",
          value: formatCurrency(
            saleDetails.order.total_paid_amount ||
              saleDetails.order.total_amount,
          ),
          accent: "text-green-400",
        },
        {
          label: "Perçu",
          value:
            saleDetails.order.amount_received !== null &&
            saleDetails.order.amount_received !== undefined
              ? formatCurrency(saleDetails.order.amount_received)
              : "—",
        },
        {
          label: "Rendu",
          value:
            saleDetails.order.change_given !== null &&
            saleDetails.order.change_given !== undefined
              ? formatCurrency(saleDetails.order.change_given)
              : "—",
        },
      ]
    : [];

  const paymentMethodsDisplay = saleDetails
    ? formatPaymentMethodList(
        saleDetails.order?.payment_methods_labels?.length
          ? saleDetails.order.payment_methods_labels
          : saleDetails.order?.payment_methods || [],
        saleDetails.order?.payment_method,
      )
    : "";

  const paymentEntries =
    saleDetails?.payments?.flatMap((payment, index) => {
      const breakdown = parsePaymentBreakdownFromNotes(payment.notes || "");
      if (breakdown.length) {
        return breakdown.map((entry, idx) => ({
          key: `${index}-${idx}`,
          method: entry.method,
          label: formatPaymentMethodLabel(entry.method),
          amount: entry.amount ?? payment.amount,
          processed_by_name: payment.processed_by_name,
        }));
      }
      return [
        {
          key: `${index}`,
          method: payment.payment_method,
          label:
            payment.payment_method_label ||
            formatPaymentMethodLabel(payment.payment_method),
          amount: payment.amount,
          processed_by_name: payment.processed_by_name,
        },
      ];
    }) || [];

  if (saleDetails && paymentEntries.length === 1) {
    const overrideLabel =
      saleDetails.order?.payment_methods_labels?.[0] ||
      saleDetails.order?.payment_methods?.[0];
    if (overrideLabel) {
      paymentEntries[0].label = formatPaymentMethodLabel(overrideLabel);
    }
  }

  const paginationWindowSize = 5;
  const paginationStart = Math.max(
    1,
    Math.min(
      pagination.current_page - Math.floor(paginationWindowSize / 2),
      Math.max(pagination.total_pages - paginationWindowSize + 1, 1),
    ),
  );
  const paginationPages = Array.from({ length: paginationWindowSize })
    .map((_, idx) => paginationStart + idx)
    .filter((pageNum) => pageNum <= pagination.total_pages);

  if (loading && sales.length === 0) {
    return (
      <div
        className={`min-h-full flex items-center justify-center ${containerBg}`}
      >
        <div className="flex items-center space-x-3 text-yellow-300">
          <div className="w-8 h-8 border-4 border-current border-t-transparent rounded-full animate-spin"></div>
          <span className="text-lg font-medium">Chargement des ventes...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-full px-4 py-8 pb-[calc(env(safe-area-inset-bottom)+8rem)] sm:px-6 lg:px-10 lg:pb-8 ${containerBg}`}>
      <SaleVoidDialog
        sale={voidSale}
        isOpen={!!voidSale}
        onClose={() => setVoidSale(null)}
        onSuccess={async () => {
          await loadSales();
          backgroundReloadFullPage(currentPageRef.current);
        }}
      />
      <SaleVoidHistoryDialog
        isOpen={showVoidHistory}
        onClose={() => setShowVoidHistory(false)}
        currentUser={currentUser}
      />
      <div className="w-full">
        {creditSaleEnabled && (
          <div className={`${cardClasses} rounded-3xl p-3 mb-6 backdrop-blur-sm transition`}>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setActiveTab("sales")}
                className={`rounded-2xl px-4 py-2.5 text-sm font-semibold transition ${
                  activeTab === "sales"
                    ? "bg-gradient-to-r from-yellow-400 to-amber-500 text-slate-900 shadow-lg"
                    : "bg-white/5 border border-white/10 text-slate-200 hover:bg-white/10"
                }`}
              >
                Ventes
              </button>
              <button
                onClick={() => setActiveTab("recovery")}
                className={`rounded-2xl px-4 py-2.5 text-sm font-semibold transition inline-flex items-center justify-center gap-2 ${
                  activeTab === "recovery"
                    ? "bg-gradient-to-r from-emerald-400 to-emerald-500 text-slate-900 shadow-lg"
                    : "bg-white/5 border border-white/10 text-slate-200 hover:bg-white/10"
                }`}
              >
                <Wallet size={16} />
                Recouvrements
              </button>
            </div>
          </div>
        )}

        {activeTab === "sales" && (
          <>
        {/* Filtres */}
        <div className={`${cardClasses} rounded-3xl p-6 mb-6 backdrop-blur-sm transition`}>
          <div className="hidden sm:flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                Filtres
              </p>
              <h2 className="text-xl font-semibold">Affiner la liste des ventes</h2>
            </div>
            <div className="flex flex-wrap gap-3">
              {canDeleteSale && (
                <button
                  onClick={() => setShowVoidHistory(true)}
                  className="inline-flex items-center gap-2 rounded-2xl border border-white/15 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:bg-white/10"
                >
                  <Clock size={16} /> Annulations
                </button>
              )}
              <button
                onClick={() => setShowExportModal(true)}
                disabled={exporting}
                className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-2 text-sm font-semibold text-white shadow-lg transition hover:opacity-90 disabled:opacity-60"
              >
                <Download size={16} />
                {exporting ? "Export..." : "Exporter (XLS)"}
              </button>
              <button
                onClick={resetFilters}
                className="inline-flex items-center gap-2 rounded-2xl border border-current px-4 py-2 text-sm font-semibold text-yellow-500 transition hover:bg-yellow-500 hover:text-white"
              >
                <RotateCcw size={16} />
                Réinitialiser
              </button>
            </div>
          </div>

          <div className="sm:hidden flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
                  Filtres
                </p>
                <h2 className="text-lg font-semibold">Suivi des ventes</h2>
              </div>
              <button
                type="button"
                onClick={() => setFiltersOpen(true)}
                className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-slate-200"
              >
                <SlidersHorizontal size={14} />
                Filtrer{activeFiltersCount > 0 ? ` (${activeFiltersCount})` : ""}
              </button>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 text-[11px] text-slate-200/80">
              <button
                type="button"
                onClick={() => setFiltersOpen(true)}
                className="shrink-0 rounded-full border border-white/10 bg-white/5 px-3 py-1.5"
              >
                {filters.start_date || filters.end_date
                  ? `Période ${formatFilterDate(filters.start_date)} -> ${formatFilterDate(filters.end_date)}`
                  : "Période"}
              </button>
              <button
                type="button"
                onClick={() => setFiltersOpen(true)}
                className="shrink-0 rounded-full border border-white/10 bg-white/5 px-3 py-1.5"
              >
                {filters.cashier_id ? "Caissier sélectionné" : "Caissier"}
              </button>
              {!isBoutique && (
                <button
                  type="button"
                  onClick={() => setFiltersOpen(true)}
                  className="shrink-0 rounded-full border border-white/10 bg-white/5 px-3 py-1.5"
                >
                  {filters.sale_type ? "Type défini" : "Type"}
                </button>
              )}
              <button
                type="button"
                onClick={() => setFiltersOpen(true)}
                className="shrink-0 rounded-full border border-white/10 bg-white/5 px-3 py-1.5"
              >
                {filters.search && filters.search.trim() ? "Recherche active" : "Recherche"}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setShowExportModal(true)}
                disabled={exporting}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 px-3 py-2 text-xs font-semibold text-white shadow-lg transition hover:opacity-90 disabled:opacity-60"
              >
                <Download size={14} />
                {exporting ? "Export..." : "XLS"}
              </button>
              <button
                onClick={resetFilters}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-current px-3 py-2 text-xs font-semibold text-yellow-500 transition hover:bg-yellow-500 hover:text-white"
              >
                <RotateCcw size={14} />
                Reset
              </button>
            </div>
          </div>

          <div className="mt-6 hidden sm:grid gap-4 lg:grid-cols-3">

	            <div className="flex flex-col space-y-2">
	              <label className="text-sm font-semibold tracking-wide text-slate-400">
	                Début (date & heure)
              </label>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => handleFilterChange("start_date", e.target.value)}
                  className={`w-full rounded-2xl px-4 py-2.5 text-sm focus:outline-none ${inputClasses}`}
                />
                <input
                  type="time"
                  value={filters.start_time}
                  onChange={(e) => handleFilterChange("start_time", e.target.value)}
                  className={`w-full rounded-2xl px-4 py-2.5 text-sm focus:outline-none ${inputClasses}`}
                  placeholder="HH:MM"
                />
              </div>
            </div>

            <div className="flex flex-col space-y-2">
              <label className="text-sm font-semibold tracking-wide text-slate-400">
                Fin (date & heure)
              </label>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => handleFilterChange("end_date", e.target.value)}
                  className={`w-full rounded-2xl px-4 py-2.5 text-sm focus:outline-none ${inputClasses}`}
                />
                <input
                  type="time"
                  value={filters.end_time}
                  onChange={(e) => handleFilterChange("end_time", e.target.value)}
                  className={`w-full rounded-2xl px-4 py-2.5 text-sm focus:outline-none ${inputClasses}`}
                  placeholder="HH:MM"
                />
              </div>
            </div>

            <div className="flex flex-col space-y-2">
              <label className="text-sm font-semibold tracking-wide text-slate-400">
                Caissier
              </label>
              <select
                value={filters.cashier_id}
                onChange={(e) => handleFilterChange("cashier_id", e.target.value)}
                className={`w-full rounded-2xl px-4 py-2.5 text-sm focus:outline-none ${inputClasses}`}
                style={selectStyle}
              >
                <option value="">Tous les caissiers</option>
                {cashiers.map((cashier) => (
                  <option key={cashier.id} value={cashier.id}>
                    {cashier.name} ({cashier.role})
                  </option>
                ))}
              </select>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:col-span-2">
              {!isBoutique && (
                <div className="flex flex-col space-y-2">
                  <label className="text-sm font-semibold tracking-wide text-slate-400">
                    Type de vente
                  </label>
                  <select
                    value={filters.sale_type}
                    onChange={(e) => handleFilterChange("sale_type", e.target.value)}
                    className={`w-full rounded-2xl px-4 py-2.5 text-sm focus:outline-none ${inputClasses}`}
                    style={selectStyle}
                  >
                    {saleTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex flex-col space-y-2">
                <label className="text-sm font-semibold tracking-wide text-slate-400">
                  Recherche (N° commande)
                </label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  placeholder="Ex: 14112584"
                  className={`w-full rounded-2xl px-4 py-2.5 text-sm focus:outline-none ${inputClasses}`}
                />
              </div>
            </div>
          </div>
        </div>

        {filtersOpen && (
          <div
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-4 backdrop-blur-sm sm:hidden"
            role="presentation"
            onClick={() => setFiltersOpen(false)}
          >
            <div
              className={`${cardClasses} w-full max-w-xl rounded-3xl p-5 max-h-[85vh] overflow-y-auto`}
              role="dialog"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
                    Filtres
                  </p>
                  <h3 className="text-lg font-semibold">Affiner la liste</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setFiltersOpen(false)}
                  className="rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-slate-200"
                >
                  Fermer
                </button>
              </div>

              <div className="mt-4 space-y-4">
                <div className="flex flex-col space-y-2">
                  <label className="text-sm font-semibold tracking-wide text-slate-400">
                    Début (date & heure)
                  </label>
                  <div className="grid grid-cols-1 gap-2">
                    <input
                      type="date"
                      value={filters.start_date}
                      onChange={(e) => handleFilterChange("start_date", e.target.value)}
                      className={`w-full rounded-2xl px-4 py-2.5 text-sm focus:outline-none ${inputClasses}`}
                    />
                    <input
                      type="time"
                      value={filters.start_time}
                      onChange={(e) => handleFilterChange("start_time", e.target.value)}
                      className={`w-full rounded-2xl px-4 py-2.5 text-sm focus:outline-none ${inputClasses}`}
                      placeholder="HH:MM"
                    />
                  </div>
                </div>

                <div className="flex flex-col space-y-2">
                  <label className="text-sm font-semibold tracking-wide text-slate-400">
                    Fin (date & heure)
                  </label>
                  <div className="grid grid-cols-1 gap-2">
                    <input
                      type="date"
                      value={filters.end_date}
                      onChange={(e) => handleFilterChange("end_date", e.target.value)}
                      className={`w-full rounded-2xl px-4 py-2.5 text-sm focus:outline-none ${inputClasses}`}
                    />
                    <input
                      type="time"
                      value={filters.end_time}
                      onChange={(e) => handleFilterChange("end_time", e.target.value)}
                      className={`w-full rounded-2xl px-4 py-2.5 text-sm focus:outline-none ${inputClasses}`}
                      placeholder="HH:MM"
                    />
                  </div>
                </div>

                <div className="flex flex-col space-y-2">
                  <label className="text-sm font-semibold tracking-wide text-slate-400">
                    Caissier
                  </label>
                  <select
                    value={filters.cashier_id}
                    onChange={(e) => handleFilterChange("cashier_id", e.target.value)}
                    className={`w-full rounded-2xl px-4 py-2.5 text-sm focus:outline-none ${inputClasses}`}
                    style={selectStyle}
                  >
                    <option value="">Tous les caissiers</option>
                    {cashiers.map((cashier) => (
                      <option key={cashier.id} value={cashier.id}>
                        {cashier.name} ({cashier.role})
                      </option>
                    ))}
                  </select>
                </div>

                {!isBoutique && (
                  <div className="flex flex-col space-y-2">
                    <label className="text-sm font-semibold tracking-wide text-slate-400">
                      Type de vente
                    </label>
                    <select
                      value={filters.sale_type}
                      onChange={(e) => handleFilterChange("sale_type", e.target.value)}
                      className={`w-full rounded-2xl px-4 py-2.5 text-sm focus:outline-none ${inputClasses}`}
                      style={selectStyle}
                    >
                      {saleTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="flex flex-col space-y-2">
                  <label className="text-sm font-semibold tracking-wide text-slate-400">
                    Recherche (N° commande)
                  </label>
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => handleFilterChange("search", e.target.value)}
                    placeholder="Ex: 14112584"
                    className={`w-full rounded-2xl px-4 py-2.5 text-sm focus:outline-none ${inputClasses}`}
                  />
                </div>
              </div>

              <div className="mt-5 flex items-center justify-between gap-3">
                <button
                  onClick={resetFilters}
                  className="inline-flex items-center gap-2 rounded-2xl border border-current px-4 py-2 text-xs font-semibold text-yellow-500 transition hover:bg-yellow-500 hover:text-white"
                >
                  <RotateCcw size={14} />
                  Réinitialiser
                </button>
                <button
                  onClick={() => setFiltersOpen(false)}
                  className="rounded-2xl bg-white/10 px-4 py-2 text-xs font-semibold text-white/90"
                >
                  Appliquer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tableau des ventes */}
        <div className={`${cardClasses} rounded-4xl overflow-hidden transition`}>
          <div className="sm:hidden space-y-3 p-4">
            {loading ? (
              <div className="flex items-center justify-center space-x-3 text-slate-400 py-6">
                <div className="w-6 h-6 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
                <span>Chargement...</span>
              </div>
            ) : sales.length === 0 ? (
              <div className="flex flex-col items-center space-y-2 text-slate-400 py-10">
                <ShoppingBag size={40} className="opacity-60" />
                <span>Aucune vente trouvée</span>
              </div>
            ) : (
              sales.map((sale) => (
                <div
                  key={sale.id}
                  className="rounded-3xl border border-white/10 bg-white/5 p-4"
                >
                  <div className="flex min-w-0 items-center justify-between gap-3">
                    <div className="min-w-0 truncate text-xs uppercase tracking-[0.3em] text-slate-400">
                      {formatDateTime(
                        sale.payment_completed_at || sale.updated_at || sale.created_at,
                      )}
                    </div>
                    <div className="shrink-0 text-sm font-semibold text-green-400">
                      {formatCurrency(sale.total_paid_amount || sale.total_amount).replace(
                        /\s*F$/,
                        "",
                      )}
                    </div>
                  </div>
                  <div className="mt-2 flex min-w-0 items-center justify-between gap-2">
                    <div className="min-w-0 truncate text-sm font-mono text-orange-400">
                      {getDisplayPaymentNumber(sale)}
                    </div>
                    {!isBoutique && (
                      <span className="shrink-0 rounded-full bg-yellow-500/10 px-3 py-1 text-[11px] font-semibold text-yellow-400">
                        {sale.type_label || getOrderTypeLabel(sale.order_type)}
                      </span>
                    )}
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-300/80">
                    {!isBoutique && (
                      <div>
                        <div className="text-slate-400">Table</div>
                        <div className="text-sm text-slate-100">{sale.table_number || "—"}</div>
                      </div>
                    )}
                    <div>
                      <div className="text-slate-400">Caissier</div>
                      <div className="text-sm text-slate-100">{sale.cashier_display || "—"}</div>
                    </div>
                    {!isBoutique && (
                      <>
                        <div>
                          <div className="text-slate-400">Visiteurs</div>
                          <div className="text-sm text-slate-100">{sale.visitor_count || "—"}</div>
                        </div>
                        <div>
                          <div className="text-slate-400">Service</div>
                          <div className="text-sm text-slate-100">
                            {sale.service_duration_minutes
                              ? formatDuration(sale.service_duration_minutes)
                              : "—"}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    {!isBoutique && (
                      <div className="text-xs text-slate-400">
                        Préparation:{" "}
                        <span className="text-slate-200">{formatPrepSummary(sale)}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleViewDetails(sale)}
                        className={actionButtonClasses}
                        title="Voir les détails"
                      >
                        <Eye size={16} />
                      </button>
                      {canDeleteSale && !sale.settlement_id && (
                        <button
                          onClick={() => handleDeleteSale(sale)}
                          className={dangerActionButtonClasses}
                          title="Annuler cette vente"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full divide-y divide-white/10">
              <thead className="bg-white/5 text-xs uppercase tracking-widest">
                <tr>
                  <th className="px-4 py-3 text-left">Date & Heure</th>
                  {!isBoutique && <th className="px-4 py-3 text-left">Table</th>}
                  <th className="px-4 py-3 text-left">N° Commande</th>
                  {!isBoutique && <th className="px-4 py-3 text-left">Type</th>}
                  <th className="px-4 py-3 text-left">Caissier</th>
                  {!isBoutique && <th className="px-4 py-3 text-left">Visiteurs</th>}
                  {!isBoutique && <th className="px-4 py-3 text-left">Durée Service</th>}
                  {!isBoutique && <th className="px-4 py-3 text-left">Temps Préparation</th>}
                  <th className="px-4 py-3 text-left">Total Payé</th>
                  <th className="px-4 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {loading ? (
                  <tr>
                    <td colSpan={isBoutique ? 5 : 10} className="px-4 py-6 text-center">
                      <div className="flex items-center justify-center space-x-3 text-slate-400">
                        <div className="w-6 h-6 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
                        <span>Chargement...</span>
                      </div>
                    </td>
                  </tr>
                ) : sales.length === 0 ? (
                  <tr>
                    <td colSpan={isBoutique ? 5 : 10} className="px-4 py-12 text-center text-slate-400">
                      <div className="flex flex-col items-center space-y-2">
                        <ShoppingBag size={40} className="opacity-60" />
                        <span>Aucune vente trouvée</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  sales.map((sale) => (
                    <tr key={sale.id} className="hover:bg-white/5 transition-colors">
                      <td className="px-4 py-3 text-sm">
                        {formatDateTime(
                          sale.payment_completed_at ||
                            sale.updated_at ||
                            sale.created_at,
                        )}
                      </td>
                    {!isBoutique && (
                      <td className="px-4 py-3 text-sm">{sale.table_number || "—"}</td>
                    )}
                    <td className="px-4 py-3 text-sm font-mono text-orange-400">
                      {getDisplayPaymentNumber(sale)}
                    </td>
                      {!isBoutique && (
                        <td className="px-4 py-3 text-sm">
                          <span className="inline-flex items-center rounded-full bg-yellow-500/10 px-3 py-1 text-xs font-semibold text-yellow-400">
                            {sale.type_label || getOrderTypeLabel(sale.order_type)}
                          </span>
                        </td>
                      )}
                      <td className={`px-4 py-3 text-sm ${tableSecondaryText}`}>
                        {sale.cashier_display || "—"}
                      </td>
                      {!isBoutique && (
                        <td className={`px-4 py-3 text-sm ${tableSecondaryText}`}>
                          {sale.visitor_count || "—"}
                        </td>
                      )}
                      {!isBoutique && (
                        <td className={`px-4 py-3 text-sm ${tableSecondaryText}`}>
                          {sale.service_duration_minutes
                            ? formatDuration(sale.service_duration_minutes)
                            : "—"}
                        </td>
                      )}
                      {!isBoutique && (
                        <td className={`px-4 py-3 text-sm ${tableSecondaryText}`}>
                          {formatPrepSummary(sale)}
                        </td>
                      )}
                      <td className="px-4 py-3 text-sm font-semibold text-green-400">
                        {formatCurrency(sale.total_paid_amount || sale.total_amount).replace(
                          /\s*F$/,
                          "",
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleViewDetails(sale)}
                            className={actionButtonClasses}
                            title="Voir les détails"
                          >
                            <Eye size={16} />
                          </button>
                          {canDeleteSale && !sale.settlement_id && (
                            <button
                              onClick={() => handleDeleteSale(sale)}
                              className={dangerActionButtonClasses}
                              title="Annuler cette vente"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex flex-col gap-3 border-t border-white/5 px-4 py-4 text-sm text-slate-400 sm:flex-row sm:items-center sm:justify-between">
              <div>
                Affichage de {((pagination.current_page - 1) * pagination.per_page) + 1} à{" "}
                {Math.min(pagination.current_page * pagination.per_page, pagination.total)} sur{" "}
                {pagination.total} ventes
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                  disabled={pagination.current_page === 1}
                  className="rounded-full border border-white/10 px-3 py-1 text-xs font-semibold text-slate-200 transition disabled:opacity-40"
                >
                  <ChevronLeft size={14} />
                </button>
                <div className="flex items-center space-x-1">
                  {paginationPages.map((pageNum) => (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                        pagination.current_page === pageNum
                          ? "bg-yellow-500 text-black"
                          : "bg-white/5 text-slate-300 hover:bg-white/10"
                      }`}
                    >
                      {pageNum}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                  disabled={pagination.current_page === pagination.total_pages}
                  className="rounded-full border border-white/10 px-3 py-1 text-xs font-semibold text-slate-200 transition disabled:opacity-40"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      </>
    )}

      {activeTab === "recovery" && (
        <div className="space-y-6">
          <div className={`${cardClasses} rounded-3xl p-6`}>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                  Suivi des créances
                </p>
                <h2 className="text-xl font-semibold">
                  Recouvrements clients
                </h2>
              </div>
              <div className="grid w-full gap-3 sm:grid-cols-2 lg:w-auto lg:grid-cols-2">
                <select
                  value={recoveryFilters.status}
                  onChange={(e) => handleRecoveryFilterChange("status", e.target.value)}
                  className={`w-full rounded-2xl px-4 py-2.5 text-sm focus:outline-none ${inputClasses}`}
                  style={selectStyle}
                >
                  <option value="all">Tous les états</option>
                  <option value="en_cours">En cours</option>
                  <option value="partiel">Partiel</option>
                  <option value="paye">Payé</option>
                </select>
                <input
                  type="text"
                  value={recoveryFilters.search}
                  onChange={(e) => handleRecoveryFilterChange("search", e.target.value)}
                  placeholder="N° commande, créancier, téléphone..."
                  className={`w-full rounded-2xl px-4 py-2.5 text-sm focus:outline-none ${inputClasses}`}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className={`${cardClasses} rounded-3xl p-5`}>
              <p className="text-xs uppercase tracking-[0.25em] text-slate-400 mb-2">
                Montant à recouvrer
              </p>
              <p className="text-3xl font-semibold text-rose-300">
                {formatCurrency(recoveryStats.total_due)}
              </p>
            </div>
            <div className={`${cardClasses} rounded-3xl p-5`}>
              <p className="text-xs uppercase tracking-[0.25em] text-slate-400 mb-2">
                Tickets à crédit
              </p>
              <p className="text-3xl font-semibold">
                {recoveryStats.total_tickets || 0}
              </p>
            </div>
            <div className={`${cardClasses} rounded-3xl p-5`}>
              <p className="text-xs uppercase tracking-[0.25em] text-slate-400 mb-2">
                Créanciers
              </p>
              <p className="text-3xl font-semibold">
                {recoveryStats.total_creditors || 0}
              </p>
            </div>
          </div>

          {recoveryError && !showRecoveryPayModal && (
            <div className="rounded-2xl border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
              {recoveryError}
            </div>
          )}
          {recoverySuccess && !showRecoveryPayModal && (
            <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">
              {recoverySuccess}
            </div>
          )}

          <div className={`${cardClasses} rounded-3xl overflow-hidden`}>
            <div className="sm:hidden space-y-3 p-4">
              {recoveryLoading ? (
                <div className="py-8 text-center text-slate-400 inline-flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin" />
                  Chargement...
                </div>
              ) : recoveryRows.length === 0 ? (
                <div className="py-8 text-center text-slate-400">
                  Aucune créance trouvée.
                </div>
              ) : (
                recoveryRows.map((row) => (
                  <div key={row.id} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs text-slate-400">
                        {formatDateTime(row.order_created_at || row.created_at)}
                      </span>
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getRecoveryStatusClass(row.status)}`}>
                        {getRecoveryStatusLabel(row.status)}
                      </span>
                    </div>
                    <p className="mt-2 text-sm font-mono text-orange-300">
                      {row.reference_number || row.order_number || `#${row.order_id}`}
                    </p>
                    <p className="mt-1 text-sm font-semibold">
                      {row.customer_display_name || row.customer_name || "—"}
                    </p>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <p className="text-slate-400">Dû</p>
                        <p className="font-semibold text-rose-300">{formatCurrency(row.amount_due)}</p>
                      </div>
                      <div>
                        <p className="text-slate-400">Payé</p>
                        <p className="font-semibold text-emerald-300">{formatCurrency(row.amount_paid)}</p>
                      </div>
                    </div>
                    <div className="mt-3 flex justify-end gap-2">
                      <button
                        onClick={() => loadRecoveryTimeline(row.id)}
                        className={actionButtonClasses}
                        title="Voir la timeline"
                      >
                        <Eye size={16} />
                      </button>
                      {Number(row.amount_due || 0) > 0.009 && (
                        <button
                          onClick={() => openRecoveryPayModal(row)}
                          className="rounded-full border border-emerald-400/30 bg-emerald-500/15 p-2 text-emerald-200 transition hover:bg-emerald-500/25"
                          title="Payer"
                        >
                          <CreditCard size={16} />
                        </button>
                      )}
                      <button
                        onClick={() => openRecoveryTicket(row)}
                        className="rounded-full border border-amber-400/30 bg-amber-400/15 p-2 text-amber-200 transition hover:bg-amber-400/25"
                        title="Voir le ticket"
                      >
                        <Receipt size={16} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="hidden sm:block overflow-x-auto">
              <table className="min-w-full divide-y divide-white/10">
                <thead className="bg-white/5 text-xs uppercase tracking-widest">
                  <tr>
                    <th className="px-4 py-3 text-left">Date & Heure</th>
                    <th className="px-4 py-3 text-left">N° Commande</th>
                    <th className="px-4 py-3 text-left">Créancier</th>
                    <th className="px-4 py-3 text-left">Caissier</th>
                    <th className="px-4 py-3 text-left">Montant dû</th>
                    <th className="px-4 py-3 text-left">Montant payé</th>
                    <th className="px-4 py-3 text-left">État</th>
                    <th className="px-4 py-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {recoveryLoading ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center">
                        <div className="inline-flex items-center gap-2 text-slate-400">
                          <Loader2 size={16} className="animate-spin" />
                          Chargement des recouvrements...
                        </div>
                      </td>
                    </tr>
                  ) : recoveryRows.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-10 text-center text-slate-400">
                        Aucune créance trouvée.
                      </td>
                    </tr>
                  ) : (
                    recoveryRows.map((row) => (
                      <tr key={row.id} className="hover:bg-white/5 transition-colors">
                        <td className="px-4 py-3 text-sm">
                          {formatDateTime(row.order_created_at || row.created_at)}
                        </td>
                        <td className="px-4 py-3 text-sm font-mono text-orange-300">
                          {row.reference_number || row.order_number || `#${row.order_id}`}
                        </td>
                        <td className={`px-4 py-3 text-sm ${tableSecondaryText}`}>
                          {row.customer_display_name || row.customer_name || "—"}
                        </td>
                        <td className={`px-4 py-3 text-sm ${tableSecondaryText}`}>
                          {row.cashier_name || "—"}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-rose-300">
                          {formatCurrency(row.amount_due)}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-emerald-300">
                          {formatCurrency(row.amount_paid)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${getRecoveryStatusClass(row.status)}`}>
                            {getRecoveryStatusLabel(row.status)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => loadRecoveryTimeline(row.id)}
                              className={actionButtonClasses}
                              title="Voir la timeline"
                            >
                              <Eye size={16} />
                            </button>
                            {Number(row.amount_due || 0) > 0.009 && (
                              <button
                                onClick={() => openRecoveryPayModal(row)}
                                className="rounded-full border border-emerald-400/30 bg-emerald-500/15 p-2 text-emerald-200 transition hover:bg-emerald-500/25"
                                title="Payer"
                              >
                                <CreditCard size={16} />
                              </button>
                            )}
                            <button
                              onClick={() => openRecoveryTicket(row)}
                              className="rounded-full border border-amber-400/30 bg-amber-400/15 p-2 text-amber-200 transition hover:bg-amber-400/25"
                              title="Voir le ticket"
                            >
                              <Receipt size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {recoveryPagination.total_pages > 1 && (
              <div className="flex items-center justify-between border-t border-white/5 px-4 py-4 text-sm text-slate-400">
                <span>
                  Page {recoveryPagination.current_page} / {recoveryPagination.total_pages}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleRecoveryPageChange(recoveryPagination.current_page - 1)}
                    disabled={recoveryPagination.current_page <= 1}
                    className="rounded-full border border-white/10 px-3 py-1 text-xs font-semibold text-slate-200 transition disabled:opacity-40"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <button
                    onClick={() => handleRecoveryPageChange(recoveryPagination.current_page + 1)}
                    disabled={recoveryPagination.current_page >= recoveryPagination.total_pages}
                    className="rounded-full border border-white/10 px-3 py-1 text-xs font-semibold text-slate-200 transition disabled:opacity-40"
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal export */}
      {showExportModal && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${modalOverlayClass}`}
        >
          <div
            className={`${modalCardClass} w-full max-w-lg overflow-hidden rounded-3xl`}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                  Export
                </p>
                <h2 className="text-xl font-semibold text-white">
                  Exporter les ventes (XLS)
                </h2>
              </div>
              <button
                onClick={() => setShowExportModal(false)}
                className={`${textMuted} hover:text-white transition-colors`}
              >
                <XCircle size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className={`${textMuted} text-sm`}>
                Choisis le format d’export souhaité.
              </p>

              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  disabled={exporting}
                  onClick={async () => {
                    setShowExportModal(false);
                    await handleExport("orders");
                  }}
                  className="rounded-2xl bg-white/10 hover:bg-white/15 border border-white/10 px-4 py-3 text-left transition disabled:opacity-60"
                >
                  <p className="text-white font-semibold">Détails par panier</p>
                  <p className={`${textMuted} text-xs mt-1`}>
                    1 ligne = 1 commande
                  </p>
                </button>

                <button
                  disabled={exporting}
                  onClick={async () => {
                    setShowExportModal(false);
                    await handleExport("items");
                  }}
                  className="rounded-2xl bg-white/10 hover:bg-white/15 border border-white/10 px-4 py-3 text-left transition disabled:opacity-60"
                >
                  <p className="text-white font-semibold">Détails par articles</p>
                  <p className={`${textMuted} text-xs mt-1`}>
                    1 ligne = 1 article / supplément
                  </p>
                </button>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowExportModal(false)}
                  className="rounded-2xl border border-white/10 px-4 py-2 text-sm font-semibold text-slate-200 hover:bg-white/5 transition"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showRecoveryTimelineModal && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${modalOverlayClass}`}>
          <div className={`${modalCardClass} w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-3xl`}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                  Historique
                </p>
                <h3 className="text-xl font-semibold">
                  Timeline de créance · {timelineTarget?.reference_number || timelineTarget?.order_number || "—"}
                </h3>
              </div>
              <button
                onClick={() => setShowRecoveryTimelineModal(false)}
                className={`${textMuted} hover:text-white transition-colors`}
              >
                <XCircle size={24} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-110px)]">
              {recoveryTimelineLoading ? (
                <div className="py-8 text-center text-slate-400 inline-flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin" />
                  Chargement de la timeline...
                </div>
              ) : recoveryTimelineRows.length === 0 ? (
                <div className="py-8 text-center text-slate-400">
                  Aucun événement enregistré.
                </div>
              ) : (
                <div className="space-y-3">
                  {recoveryTimelineRows.map((event) => (
                    <div
                      key={event.id}
                      className={`${modalSectionClass} rounded-2xl p-4`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold">
                            {event.message || event.type}
                          </p>
                          <p className={`text-xs ${textMuted}`}>
                            {event.actor_name || "Système"}
                          </p>
                        </div>
                        <p className={`text-xs ${textMuted}`}>
                          {formatDateTime(event.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showRecoveryPayModal && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${modalOverlayClass}`}>
          <div className={`${modalCardClass} w-full max-w-xl overflow-hidden rounded-3xl`}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                  Recouvrement
                </p>
                <h3 className="text-xl font-semibold">Valider un paiement de créance</h3>
              </div>
              <button
                onClick={() => setShowRecoveryPayModal(false)}
                className={`${textMuted} hover:text-white transition-colors`}
              >
                <XCircle size={24} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className={`${modalSectionClass} rounded-2xl p-4`}>
                <p className={`text-xs uppercase ${textMuted}`}>Cible</p>
                <p className="font-semibold mt-1">
                  {recoveryPayTarget?.customer_display_name ||
                    recoveryPayTarget?.customer_name ||
                    recoveryPayTarget?.reference_number ||
                    `#${recoveryPayTarget?.order_id || "—"}`}
                </p>
                <p className={`text-sm mt-1 ${textMuted}`}>
                  Montant dû:{" "}
                  <span className="text-rose-300 font-semibold">
                    {formatCurrency(recoveryPayTarget?.amount_due || recoveryPayTarget?.due_amount || 0)}
                  </span>
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className={`mb-2 block text-xs uppercase tracking-[0.2em] ${textMuted}`}>
                    Montant à payer
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={recoveryPayAmount}
                    onChange={(e) => setRecoveryPayAmount(e.target.value)}
                    className={`w-full rounded-2xl px-4 py-2.5 text-sm focus:outline-none ${inputClasses}`}
                  />
                </div>
                <div>
                  <label className={`mb-2 block text-xs uppercase tracking-[0.2em] ${textMuted}`}>
                    Moyen de paiement
                  </label>
                  <select
                    value={recoveryPayMethod}
                    onChange={(e) => setRecoveryPayMethod(e.target.value)}
                    className={`w-full rounded-2xl px-4 py-2.5 text-sm focus:outline-none ${inputClasses}`}
                    style={selectStyle}
                  >
                    <option value="">Sélectionner…</option>
                    {recoveryPaymentMethods.map((method) => (
                      <option key={method.code || method.id} value={method.code || method.id}>
                        {method.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className={`mb-2 block text-xs uppercase tracking-[0.2em] ${textMuted}`}>
                  Notes (optionnel)
                </label>
                <textarea
                  rows={2}
                  value={recoveryPayNote}
                  onChange={(e) => setRecoveryPayNote(e.target.value)}
                  placeholder="Note de recouvrement"
                  className={`w-full rounded-2xl px-4 py-2.5 text-sm focus:outline-none ${inputClasses}`}
                />
              </div>
              <div>
                <label className={`mb-2 block text-xs uppercase tracking-[0.2em] ${textMuted}`}>
                  Code utilisateur (validation)
                </label>
                <input
                  type="password"
                  value={recoveryPayPin}
                  onChange={(e) => setRecoveryPayPin(e.target.value)}
                  placeholder="Code caissier/admin/manager"
                  className={`w-full rounded-2xl px-4 py-2.5 text-sm focus:outline-none ${inputClasses}`}
                />
              </div>
              <div className="rounded-2xl border border-emerald-400/20 bg-emerald-400/10 px-4 py-3 text-sm text-emerald-100">
                <p className="font-semibold text-emerald-200 mb-1">Récapitulatif</p>
                <p>
                  Paiement de <span className="font-semibold">{formatCurrency(recoveryPayAmount || 0)}</span>{" "}
                  via <span className="font-semibold">{formatPaymentMethodLabel(recoveryPayMethod)}</span>.
                </p>
              </div>
              {recoveryError && (
                <div className="rounded-2xl border border-rose-400/30 bg-rose-500/10 px-4 py-2 text-sm text-rose-200">
                  {recoveryError}
                </div>
              )}
              {recoverySuccess && (
                <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-200">
                  {recoverySuccess}
                </div>
              )}
            </div>
            <div className="px-6 pb-6 flex justify-end gap-3">
              <button
                onClick={() => setShowRecoveryPayModal(false)}
                className="rounded-2xl border border-white/15 bg-white/5 px-4 py-2.5 font-semibold text-white/80 hover:bg-white/10"
              >
                Annuler
              </button>
              <button
                onClick={submitRecoveryPayment}
                disabled={recoverySubmitting}
                className="rounded-2xl bg-gradient-to-r from-emerald-400 to-emerald-500 px-4 py-2.5 font-semibold text-slate-900 disabled:opacity-60 inline-flex items-center gap-2"
              >
                {recoverySubmitting && <Loader2 size={16} className="animate-spin" />}
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal des détails */}
      {showDetailsModal && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${modalOverlayClass}`}>
          <div className={`${modalCardClass} w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-3xl`}>
            <div className="flex justify-between items-center px-6 py-4 border-b border-white/10">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                  Détails de la vente
                </p>
                <h2 className="text-2xl font-semibold flex items-center gap-3">
                  <Receipt size={24} className="text-green-400" />
                  {selectedSale ? getDisplayPaymentNumber(selectedSale) : "Ticket"}
                </h2>
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                className={`${textMuted} hover:text-white transition-colors`}
              >
                <XCircle size={24} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)] space-y-6">
              {loadingDetails ? (
                <div className={`flex items-center justify-center py-12 ${textMuted}`}>
                  <div className="w-8 h-8 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin mr-3"></div>
                  Chargement des détails...
                </div>
              ) : saleDetails ? (
                <div className="space-y-6">
                  <div className="grid gap-4 lg:grid-cols-2">
                    <div className={`${modalSectionClass} rounded-2xl p-4`}>
                      <h3 className={`text-lg font-semibold ${textPrimary} mb-4 flex items-center gap-2`}>
                        <Calendar size={20} className="text-blue-400" />
                        Informations générales
                      </h3>
                      <div className="grid gap-3 sm:grid-cols-2">
                        {generalInfoFields.map((field) => (
                          <div key={field.label} className={`${modalSubCardClass} rounded-2xl p-3 text-sm`}>
                            <p className={`text-xs uppercase ${textMuted}`}>{field.label}</p>
                            <p className="font-semibold">{field.value}</p>
                            {field.extra && (
                              <span className={`mt-2 inline-flex rounded-full px-2 py-1 text-[11px] ${pillClass}`}>
                                {field.extra}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className={`${modalSectionClass} rounded-2xl p-4`}>
                      <h3 className={`text-lg font-semibold ${textPrimary} mb-4 flex items-center gap-2`}>
                        <CreditCard size={20} className="text-green-400" />
                        Paiement
                      </h3>
                      <div className="grid gap-3 sm:grid-cols-3">
                        {paymentSummaryStats.map((stat) => (
                          <div key={stat.label} className={`${modalSubCardClass} rounded-2xl p-3 text-sm`}>
                            <p className={`text-xs uppercase ${textMuted}`}>{stat.label}</p>
                            <p className={`text-base font-semibold ${stat.accent || textPrimary}`}>
                              {stat.value}
                            </p>
                          </div>
                        ))}
                      </div>
                      {paymentMethodsDisplay && (
                          <p className={`mt-4 text-xs ${textMuted}`}>
                            Mode(s) :{" "}
                            {paymentMethodsDisplay}
                          </p>
                        )}
                      {financials.discountTotal > 0.01 && (
                        <div className="mt-3 space-y-1 text-sm">
                          {resolvedSubtotal > 0 && (
                            <div className={`flex justify-between ${textMuted}`}>
                              <span>Sous-total</span>
                              <span className="font-mono font-semibold text-white">
                                {formatCurrency(resolvedSubtotal)}
                              </span>
                            </div>
                          )}
                          {financials.discountTotal > 0.01 && (
                            <div className="flex justify-between text-red-400">
                              <span>Réduction</span>
                              <span className="font-mono font-semibold">
                                -{formatCurrency(financials.discountTotal)}
                              </span>
                            </div>
                          )}
                          <div className={`flex justify-between ${textMuted}`}>
                            <span>Total facture (après remise)</span>
                            <span className="font-mono font-semibold text-green-400">
                              {formatCurrency(
                                financials.totalFinal || saleDetails.order.total_amount,
                              )}
                            </span>
                          </div>
                        </div>
                      )}
                      <div className="my-4 h-px bg-white/10" />
                      <div className="space-y-3">
                        {paymentEntries.map((entry) => {
                          const PaymentIcon = getPaymentMethodIcon(entry.method);
                          return (
                            <div key={entry.key} className={`${modalSubCardClass} rounded-2xl p-3 flex items-center justify-between`}>
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center space-x-3">
                                  <div className="rounded-full bg-white/10 p-2">
                                    <PaymentIcon size={18} className="text-slate-200" />
                                  </div>
                                  <div className="flex flex-col">
                                    <span className={`${textPrimary} font-medium`}>
                                      {entry.label}
                                    </span>
                                    {entry.processed_by_name && (
                                      <span className={`text-xs ${textMuted}`}>
                                        {entry.processed_by_name}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <span className="text-green-400 font-semibold">
                                {formatCurrency(entry.amount)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className={`${modalSectionClass} rounded-2xl p-4`}>
                    <h3 className={`text-lg font-semibold ${textPrimary} mb-4 flex items-center gap-2`}>
                      <ShoppingBag size={20} className="text-purple-400" />
                      Articles commandés
                    </h3>
                    <div className="space-y-3">
                      {saleDetails.items
                        .filter(
                          (item) =>
                            String(item?.status || "").toLowerCase() !==
                            "cancelled",
                        )
                        .map((item, index) => (
                        <div key={index} className={`${modalSubCardClass} rounded-2xl p-4`}>
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center space-x-3">
                              {!isBoutique && (
                                <span className="text-2xl">{item.emoji || "🍽️"}</span>
                              )}
                              <div>
                                <span className={`${textPrimary} font-medium`}>
                                  {formatItemName(item)}
                                </span>
                                <div className={`text-sm ${textMuted}`}>
                                  Quantité: {item.quantity} × {formatCurrency(item.unit_price)}
                                </div>
                              </div>
                            </div>
                            <span className="text-green-400 font-semibold">
                              {formatCurrency(computeItemTotalWithExtras(item))}
                            </span>
                          </div>
                          {item.extras && item.extras.length > 0 && (
                            <div className="mt-3 pl-4 border-l-2 border-white/10">
                              <div className={`text-sm ${textMuted} mb-2`}>Extras :</div>
                              {item.extras.map((extra, extraIndex) => (
                                <div key={extraIndex} className={`flex justify-between text-sm ${textMuted}`}>
                                  <span>
                                    + {extra.extra_name} (×{extra.quantity})
                                  </span>
                                  <span>{formatCurrency(extra.total_price)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {saleDetails.adjustments.length > 0 && (
                    <div className={`${modalSectionClass} rounded-2xl p-4`}>
                      <h3
                        className={`text-lg font-semibold ${textPrimary} mb-4 flex items_center gap-2`}
                      >
                        <Minus size={20} className="text-orange-400" />
                        Ajustements appliqués
                      </h3>
                      <div className="space-y-3">
                        {saleDetails.adjustments.map((adjustment, index) => (
                          <div
                            key={index}
                            className={`${modalSubCardClass} rounded-2xl p-3`}
                          >
                            <div className="flex justify-between items-center mb-2">
                              <div className="flex items-center space-x-2">
                                {adjustment.type === "discount" ? (
                                  <Minus size={16} className="text-red-400" />
                                ) : (
                                  <Plus size={16} className="text-green-400" />
                                )}
                                <span className={`${textPrimary} font-medium`}>
                                  {adjustment.name}
                                </span>
                              </div>
                              <span
                                className={`font-semibold ${
                                  adjustment.type === "discount"
                                    ? "text-red-400"
                                    : "text-green-400"
                                }`}
                              >
                                {adjustment.type === "discount" ? "-" : "+"}
                                {formatCurrency(adjustment.amount)}
                              </span>
                            </div>
                            <div className={`text-sm ${textMuted}`}>
                              Appliqué par: {adjustment.applied_by_name} (
                              {adjustment.applied_by_role})
                            </div>
                            {adjustment.notes && (
                              <div className={`text-sm ${textMuted} mt-1`}>
                                Note: {adjustment.notes}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(() => {
                    const hasLogs =
                      Array.isArray(saleDetails.cancellation_logs) &&
                      saleDetails.cancellation_logs.length > 0;
                    const cancelledFromItems = Array.isArray(saleDetails.items)
                      ? saleDetails.items
                          .filter(
                            (item) =>
                              String(item?.status || "").toLowerCase() ===
                              "cancelled",
                          )
                          .map((item) => ({
                            product_name: item.product_name || item.name,
                            quantity: item.quantity,
                            unit_price: item.unit_price,
                            total_price: computeItemTotalWithExtras(item),
                            cancelled_by_name: "—",
                            cancellation_reason: item.cancellation_reason,
                            manager_authorized:
                              item.manager_auth_required ||
                              item.manager_code_used,
                          }))
                      : [];

                    const cancellations = hasLogs
                      ? saleDetails.cancellation_logs
                      : cancelledFromItems;
                    if (!cancellations.length) return null;

                    return (
                      <div className={`${dangerCardClass} rounded-2xl p-4`}>
                        <h3
                          className={`text-lg font-semibold ${textPrimary} mb-4 flex items-center gap-2`}
                        >
                          <XCircle size={20} className="text-red-400" />
                          Articles annulés
                        </h3>
                        <div className="space-y-3">
                          {cancellations.map((cancellation, index) => (
                            <div
                              key={index}
                              className="rounded-2xl border border-red-500/40 p-3"
                            >
                              <div className="flex justify-between items-start mb-2">
                                <div>
                                  <span className={`${textPrimary} font-medium`}>
                                    {cancellation.product_name}
                                  </span>
                                  <div className="text-sm text-red-300">
                                    Quantité: {cancellation.quantity} ×{" "}
                                    {formatCurrency(cancellation.unit_price)}
                                  </div>
                                </div>
                                <span className="text-red-400 font-semibold">
                                  -{formatCurrency(cancellation.total_price)}
                                </span>
                              </div>
                              <div className={`text-sm ${textMuted}`}>
                                Annulé par: {cancellation.cancelled_by_name}
                              </div>
                              {cancellation.cancellation_reason && (
                                <div className={`text-sm ${textMuted} mt-1`}>
                                  Motif: {cancellation.cancellation_reason}
                                </div>
                              )}
                              {cancellation.manager_authorized && (
                                <div className="text-sm text-green-400 mt-1 flex items-center gap-1">
                                  <CheckCircle size={14} />
                                  Validé par un manager
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  <div className={`${modalSectionClass} rounded-2xl p-4`}>
                    <h3
                      className={`text-lg font-semibold ${textPrimary} mb-4 flex items-center gap-2`}
                    >
                      <DollarSign size={20} className="text_green-400" />
                      Récapitulatif financier
                    </h3>
                    <div className="space-y-2">
                      <div className={`flex justify-between ${textMuted}`}>
                        <span>Sous-total :</span>
                        <span>{formatCurrency(resolvedSubtotal)}</span>
                      </div>
                      {financials.discountTotal > 0.01 && (
                        <div className="flex justify-between text-red-400">
                          <span>Réduction :</span>
                          <span>-{formatCurrency(financials.discountTotal)}</span>
                        </div>
                      )}
                      {financials.taxAmount > 0.01 && (
                        <div className={`flex justify-between ${textMuted}`}>
                          <span>Taxes :</span>
                          <span>{formatCurrency(financials.taxAmount)}</span>
                        </div>
                      )}
                      <div className="border-t border-white/10 pt-2 flex justify-between text-lg font-semibold text-green-400">
                        <span>Total final :</span>
                        <span>
                          {formatCurrency(
                            financials.totalFinal || saleDetails.order.total_amount,
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={`text-center py-12 ${textMuted}`}>
                  Impossible de charger les détails de cette vente.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    <ReservesDashboard />
  </div>
  );
};

export default SalesInterface;
